import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import {
  ApplyRouterConfigRequest,
  CreateRouterConfigData,
  RouterConfig,
} from '@/shared/types/routerTypes';
import { PaginatedResponse, SingleResponse } from '@/shared/types/responseTypes';

export const ROUTERS_QUERY_KEY = ['account-routers'];
export const ROUTER_CONFIGS_QUERY_KEY = ['account-router-configs-paginated'];

export const usePaginatedRouterConfigs = () => {
  return useInfiniteQuery({
    queryKey: ROUTER_CONFIGS_QUERY_KEY,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await apiClient.get<PaginatedResponse<RouterConfig>>(
        `/api/v1/routers/configs`,
        { params: { page: pageParam } }
      );
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.isLastPage ? undefined : lastPageParam + 1,
  });
};

export const useCreateRouterConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateRouterConfigData) => {
      return apiClient.post<SingleResponse<RouterConfig>>('/api/v1/routers/configs', { body });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROUTER_CONFIGS_QUERY_KEY });
    },
    onError: (error: Error) => error,
  });
};

export const useAssignRouterConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ routerIds, configId }: ApplyRouterConfigRequest) => {
      return apiClient.put<APIMessageResponse>(`/api/v1/routers/configs/assign`, { body: { routerIds, configId: configId ?? undefined } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROUTERS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ROUTER_CONFIGS_QUERY_KEY });
    },
    onError: (error: Error) => error,
  });
};

export const useRebootRouter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ routerId }: { routerId: string }) => {
      return apiClient.post<APIMessageResponse>(`/api/v1/routers/${routerId}/reboot`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROUTERS_QUERY_KEY });
    },
    onError: (error: Error) => error,
  });
};